import * as React from 'react';
import styled from 'styled-components';

import { Layer, MapMouseEvent, Source, useMap } from 'react-map-gl/mapbox';
import { FeatureCollection } from 'geojson';
import { featureCollection } from '@turf/helpers';

import { Mouse } from '@longline/aqua-ui/controls/Mouse';
import { Key } from '@longline/aqua-ui/controls/Key';

import { POLYGON_FILL_COLOR_VALID, POLYGON_LINE_COLOR_VALID } from '../EditorConfig';
import { PolygonUtils } from '../../util/PolygonUtils';
import { IPoint } from '../../types/IPoint';
import { Guide } from '../../ui/Guide';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { MAX_STRUCTURE_RADIUS } from '../../types/Constants';

interface IProps {
  /** Fired when circle builder is cancelled. */
  onCancel: () => void;
  /** Fired when circle builder completes. */
  onComplete: (points: IPoint, radius: number) => void;
}

const CircleBuilder = (props: IProps) => {
  const map = useMap().current!;
  // Current point position
  const pointRef = React.useRef<IPoint | null>(null);
  // Current radius
  const radiusRef = React.useRef<number>(0);
  // Radius state; update to cause component to rerender.
  const [radius, setRadius] = React.useState<number | null>(null);

  const handleKeydown = (e: KeyboardEvent) => {
    switch(e.code) {
      case 'Enter':
      case 'NumpadEnter':
        submit();
        break;
      case 'Escape':
        props.onCancel();
        break;
    }
  }  

  const handleMouseMove = (e: MapMouseEvent) => {
    if(pointRef.current === null) return;
    radiusRef.current = PolygonUtils.distance(pointRef.current, e.lngLat)
    setRadius(radiusRef.current);
  }

  const handleClick = (e: MapMouseEvent) => {
    e.preventDefault();
    if(pointRef.current === null) {
      pointRef.current = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      setRadius(0);
    } else {
      submit();
    }
  }

  const submit = () => {
    if(pointRef.current !== null) {
      props.onComplete(pointRef.current, radiusRef.current);
    }
  }

  const mount = () => {
    // Start listening to keyboard:
    document.addEventListener('keydown', handleKeydown);         
    map.on('mousemove', handleMouseMove);
    map.on('click', handleClick);
    map.getCanvas().style.cursor = 'crosshair';
    pointRef.current = null;
  }  

  const unmount = () => {
    // Stop listing to keyboard:
    document.removeEventListener('keydown', handleKeydown);      
    map.off('mousemove', handleMouseMove);
    map.off('click', handleClick);
    map.getCanvas().style.cursor = '';
  }

  React.useEffect(() => {
    mount();
    return unmount
  }, []);

  // Create a circle of NUM_CIRCLE_POINTS points.
  const getCircleJSON = (): FeatureCollection | null => {
    if(!pointRef.current || radius === null) return null;
    return featureCollection([PolygonUtils.getCirclePolygon(pointRef.current, radius)]);
  }

  return (
    <>
      {pointRef.current == null && <Guide x={16} y={64} title="Adding a circular structure">
        <ul>
          <li>To add a circular structure, move the cursor to the structure's center point. 
          Click <Mouse size={14} left/> the mouse once.</li>
          <li><Key value="ESC"/> cancels.</li>
        </ul>
      </Guide>}
      {pointRef.current != null && <Guide x={16} y={64} title="Adding a circular structure">
        <ul>
          <li>Move the mouse to set a radius, then click <Mouse size={14} left/> or <Key value="ENTER"/> to confirm.</li>
          <li><Key value="ESC"/> cancels.</li>
        </ul>
      </Guide>}      
      <Tooltip $radius={radius ?? 0}>
        Diameter: <NumberFormatter value={Math.min(MAX_STRUCTURE_RADIUS, radius ?? 0)*2} decimals={2}/>m
      </Tooltip>
      <Source type="geojson" data={getCircleJSON() ?? undefined}>
        <Layer 
          type="fill"
          paint={{
            "fill-color": POLYGON_FILL_COLOR_VALID,
            "fill-opacity": 0.2
          }}
        />        
        <Layer 
          type="line"
          paint={{
            "line-color": POLYGON_LINE_COLOR_VALID,
            "line-width": 1.5,
            "line-dasharray": [ 2, 1 ]
          }}
        />
      </Source>
    </>
  );
}

const Tooltip = styled.div<{ $radius: number }>`
  position: absolute;
  left: 50%;
  bottom: 40px;
  transform: translateX(-50%);
  background-color: #111;
  color: ${p => p.$radius > MAX_STRUCTURE_RADIUS ? p.theme.colors.negative : '#fff'};
  padding: 8px 16px;
  border-radius: ${p => p.theme.radius.normal}px;
`

export { CircleBuilder }
